import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Bird } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { BannerCarousel, EmptyState, SearchBar, Skeleton } from '@nokowazzi/ui';
import type { BannerCarouselBanner } from '@nokowazzi/ui';
import { ProductGrid, ProductGridSkeleton } from '../features/home/components/ProductGrid';
import { QueryErrorState } from '../features/home/components/QueryErrorState';
import { RequestList } from '../features/home/components/RequestList';
import { pickI18nText, toLocale } from '../features/home/format';
import { useHomeQuery } from '../features/home/queries';
import type { HomeBannerDto } from '../features/home/types';
import { productImageUrl } from '../features/home/urls';

export const Route = createFileRoute('/_tabs/')({
  component: HomeScreen,
});

function HomeScreen() {
  const { t, i18n } = useTranslation();
  const locale = toLocale(i18n.language);
  const navigate = useNavigate();
  const homeQuery = useHomeQuery();

  const goSearch = () => void navigate({ to: '/search' });
  const goProduct = (productId: string) =>
    void navigate({ to: '/products/$productId', params: { productId } });
  const goRequest = (requestId: string) =>
    void navigate({ to: '/sourcing/$requestId', params: { requestId } });

  const bannerClick = (banner: HomeBannerDto): (() => void) | undefined => {
    const value = banner.link_value;
    if (value === null || value === '') return undefined;
    switch (banner.link_type) {
      case 'product':
        return () => goProduct(value);
      case 'request':
        return () => goRequest(value);
      case 'url':
        return () => {
          window.open(value, '_blank', 'noopener,noreferrer');
        };
      default:
        return undefined;
    }
  };

  const banners: BannerCarouselBanner[] = (homeQuery.data?.banners ?? []).flatMap((banner) => {
    const path = pickI18nText(banner.image_i18n, locale);
    if (path === '') return [];
    return [
      {
        id: banner.id,
        imageUrl: productImageUrl(path),
        alt: banner.title,
        onClick: bannerClick(banner),
      },
    ];
  });

  const featured = homeQuery.data?.featuredProducts ?? [];
  const requests = homeQuery.data?.popularRequests ?? [];
  const isAllEmpty =
    homeQuery.isSuccess && banners.length === 0 && featured.length === 0 && requests.length === 0;

  return (
    <div className="px-5 pt-[calc(var(--safe-top)+16px)] pb-6">
      <h1 className="text-title1 text-fg">{t('home.greeting')}</h1>
      <p className="text-body text-fg-secondary mt-1">{t('home.tagline')}</p>

      <SearchBar
        className="mt-4"
        value=""
        onValueChange={goSearch}
        clearLabel={t('actions.clear')}
        placeholder={t('home.searchPlaceholder')}
        readOnly
        onFocus={goSearch}
        onClick={goSearch}
      />

      {homeQuery.isPending ? (
        <div className="mt-5 flex flex-col gap-8">
          <Skeleton className="w-full rounded-lg" style={{ aspectRatio: '21 / 9' }} />
          <div className="flex flex-col gap-3">
            <Skeleton variant="text" className="w-24" />
            <ProductGridSkeleton />
          </div>
        </div>
      ) : homeQuery.isError ? (
        <QueryErrorState
          className="mt-10"
          error={homeQuery.error}
          onRetry={() => void homeQuery.refetch()}
        />
      ) : isAllEmpty ? (
        <EmptyState
          className="mt-10"
          icon={<Bird size={48} strokeWidth={1.5} />}
          title={t('home.empty')}
          description={t('home.emptyDescription')}
        />
      ) : (
        <div className="mt-5 flex flex-col gap-8">
          {banners.length > 0 ? <BannerCarousel banners={banners} /> : null}

          {featured.length > 0 ? (
            <section>
              <h2 className="text-title3 text-fg mb-3">{t('home.featured')}</h2>
              <ProductGrid products={featured} onSelect={goProduct} />
            </section>
          ) : null}

          {requests.length > 0 ? (
            <section>
              <h2 className="text-title3 text-fg mb-3">{t('home.popularRequests')}</h2>
              <RequestList requests={requests} onSelect={goRequest} />
            </section>
          ) : null}
        </div>
      )}
    </div>
  );
}
